import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { UiAvatarComponent } from '../../atoms/avatar/avatar.component';
import { UiButtonComponent } from '../../atoms/button/button.component';
import { UiCheckboxComponent } from '../../atoms/checkbox/checkbox.component';
import { UiIdentityPhotoCaptureComponent } from '../../atoms/identity-photo-capture/identity-photo-capture.component';
import { UiInputComponent } from '../../atoms/input/input.component';
import { UiPhoneInputComponent } from '../../atoms/phone-input/phone-input.component';
import { UiSelectComponent, UiSelectOption } from '../../atoms/select/select.component';
import { UiTagComponent } from '../../atoms/tag/tag.component';
import { UiTooltipDirective } from '../../directives/tooltip/tooltip.directive';
import { UiCompactListComponent } from '../../molecules/compact-list/compact-list.component';
import { UiCompactListColumn } from '../../molecules/compact-list/compact-list.types';
import { UiDrawerComponent } from '../../molecules/drawer/drawer.component';
import { UiFormSectionComponent } from '../../molecules/form-section/form-section.component';
import {
  UiDataTableActionMenuState,
  UiDataTableActionsMenuComponent,
} from '../data-table/data-table-actions-menu.component';
import { UiDataTablePaginationComponent } from '../data-table/data-table-pagination.component';
import {
  getPagedRows,
  getRangeEnd,
  getRangeStart,
  getTotalPages,
  getVisiblePages,
} from '../data-table/data-table.pagination';
import { RowAction } from '../data-table/data-table.types';
import {
  UiContactChannel,
  UiContactListActionEvent,
  UiContactListItem,
  UiContactNotificationOption,
  UiContactSavePayload,
} from './contact-list.types';

const PAGE_SIZES = [10, 25, 50, 100];

const PAGE_SIZE_OPTIONS: UiSelectOption[] = PAGE_SIZES.map(size => ({
  id: String(size),
  label: `${size} / página`,
  value: size,
}));

const CHANNEL_TYPE_OPTIONS: UiSelectOption[] = [
  { id: 'landline', label: 'Teléfono fijo', value: 'landline' },
  { id: 'mobile', label: 'Celular', value: 'mobile' },
  { id: 'email', label: 'Correo electrónico', value: 'email' },
  { id: 'other', label: 'Otro', value: 'other' },
];

const CHANNEL_COLUMNS: UiCompactListColumn<UiContactChannel>[] = [
  { key: 'typeLabel', header: 'Tipo', width: 'minmax(8rem, 1fr)' },
  { key: 'value', header: 'Dato', width: 'minmax(8rem, 1.4fr)' },
];

const DEFAULT_ACTIONS: readonly RowAction[] = [
  { key: 'ver', label: 'Ver' },
  { key: 'editar', label: 'Editar' },
  {
    key: 'inactivar',
    label: 'Inactivar',
    visible: row => (row as UiContactListItem).status === 'Activo',
  },
  {
    key: 'activar',
    label: 'Activar',
    visible: row => (row as UiContactListItem).status === 'Inactivo',
  },
  { key: 'eliminar', label: 'Eliminar', destructive: true },
];

/**
 * Organismo: listado de contactos de una entidad en cards.
 * Presentacional: filtra y pagina en cliente; emite acciones y Excel.
 */
@Component({
  selector: 'ui-contact-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiAvatarComponent,
    UiButtonComponent,
    UiCheckboxComponent,
    UiCompactListComponent,
    UiDataTableActionsMenuComponent,
    UiDataTablePaginationComponent,
    UiDrawerComponent,
    UiFormSectionComponent,
    UiIdentityPhotoCaptureComponent,
    UiInputComponent,
    UiPhoneInputComponent,
    UiSelectComponent,
    UiTagComponent,
    UiTooltipDirective,
  ],
  templateUrl: './contact-list.component.html',
  styleUrl: './contact-list.component.scss',
  host: { class: 'ui-contact-list-host' },
})
export class UiContactListComponent {
  readonly items = input<readonly UiContactListItem[]>([]);
  readonly actionItems = input<readonly RowAction[]>([]);
  readonly showNewButton = input(false);
  readonly newLabel = input('Nuevo');
  readonly notificationOptions = input<readonly UiContactNotificationOption[]>([]);

  readonly exportExcel = output<void>();
  readonly newClick = output<void>();
  readonly contactSave = output<UiContactSavePayload>();
  readonly actionClick = output<UiContactListActionEvent>();

  protected readonly search = signal('');
  protected readonly currentPage = signal(0);
  protected readonly currentSize = signal(10);
  protected readonly actionMenu = signal<UiDataTableActionMenuState | null>(null);
  protected readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  protected readonly channelTypeOptions = CHANNEL_TYPE_OPTIONS;
  protected readonly channelColumns = CHANNEL_COLUMNS;
  protected readonly formOpen = signal(false);
  protected readonly channelDrawerOpen = signal(false);
  protected readonly photoResetKey = signal(0);
  protected readonly photoFile = signal<File | null>(null);
  protected readonly fullName = signal('');
  protected readonly department = signal('');
  protected readonly position = signal('');
  protected readonly channels = signal<UiContactChannel[]>([]);
  protected readonly selectedNotificationIds = signal<readonly string[]>([]);
  protected readonly editingChannelId = signal<string | null>(null);
  protected readonly channelType = signal<UiSelectOption | null>(null);
  protected readonly channelValue = signal('');
  protected readonly channelDrawerTitle = computed(() =>
    this.editingChannelId() ? 'Editar medio de contacto' : 'Nuevo medio de contacto',
  );
  protected readonly channelTypeId = computed(() => String(this.channelType()?.id ?? ''));
  protected readonly isPhoneChannel = computed(() => {
    const id = this.channelTypeId();
    return id === 'landline' || id === 'mobile';
  });
  protected readonly isEmailChannel = computed(() => this.channelTypeId() === 'email');
  private channelSeq = 0;
  protected readonly selectedSizeOption = computed(
    () =>
      this.pageSizeOptions.find(option => Number(option.value) === this.currentSize()) ??
      this.pageSizeOptions[0],
  );

  protected readonly resolvedActions = computed(() =>
    this.actionItems().length ? this.actionItems() : DEFAULT_ACTIONS,
  );

  protected readonly filteredItems = computed(() => {
    const query = this.search().trim().toLocaleLowerCase('es');
    const items = this.items();
    if (!query) return items;

    return items.filter(item =>
      [
        item.name,
        item.email,
        item.phone,
        item.department,
        item.position,
        String(item.notifications),
      ]
        .join(' ')
        .toLocaleLowerCase('es')
        .includes(query),
    );
  });

  protected readonly total = computed(() => this.filteredItems().length);
  protected readonly totalPages = computed(() =>
    getTotalPages(this.total(), this.currentSize()),
  );
  protected readonly visiblePages = computed(() =>
    getVisiblePages(this.totalPages(), this.safePage()),
  );
  protected readonly rangeStart = computed(() =>
    getRangeStart(this.total(), this.safePage(), this.currentSize()),
  );
  protected readonly rangeEnd = computed(() =>
    getRangeEnd(this.total(), this.safePage(), this.currentSize()),
  );
  protected readonly pagedItems = computed(() =>
    getPagedRows(this.filteredItems(), this.safePage(), this.currentSize()),
  );

  constructor() {
    const onScroll = () => this.closeActionMenu();
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    inject(DestroyRef).onDestroy(() =>
      document.removeEventListener('scroll', onScroll, { capture: true }),
    );
  }

  protected onNew(): void {
    this.closeActionMenu();
    this.resetForm();
    this.formOpen.set(true);
    this.newClick.emit();
  }

  protected onFormClose(): void {
    this.channelDrawerOpen.set(false);
    this.formOpen.set(false);
  }

  protected onContactSave(): void {
    this.contactSave.emit({
      photo: this.photoFile(),
      name: this.fullName().trim(),
      department: this.department().trim(),
      position: this.position().trim(),
      channels: this.channels(),
      notificationIds: this.selectedNotificationIds(),
    });
    this.onFormClose();
  }

  protected onPhotoSelected(file: File): void {
    this.photoFile.set(file);
  }

  protected onPhotoCleared(): void {
    this.photoFile.set(null);
  }

  protected isNotificationSelected(id: string): boolean {
    return this.selectedNotificationIds().includes(id);
  }

  protected onNotificationChange(id: string, checked: boolean): void {
    this.selectedNotificationIds.update(ids =>
      checked ? [...ids, id] : ids.filter(current => current !== id),
    );
  }

  protected onNewChannel(): void {
    this.resetChannelForm();
    this.channelDrawerOpen.set(true);
  }

  protected onEditChannel(row: UiContactChannel): void {
    this.editingChannelId.set(row.id);
    this.channelType.set(
      CHANNEL_TYPE_OPTIONS.find(option => option.id === row.type) ?? null,
    );
    this.channelValue.set(row.value);
    this.channelDrawerOpen.set(true);
  }

  protected onDeleteChannel(row: UiContactChannel): void {
    this.channels.update(list => list.filter(item => item.id !== row.id));
  }

  protected onChannelSave(): void {
    const type = this.channelType();
    const value = this.channelValue().trim();
    if (!type || !value) return;

    const next: UiContactChannel = {
      id: this.editingChannelId() ?? `ch-${++this.channelSeq}`,
      type: String(type.id),
      typeLabel: type.label,
      value,
    };

    this.channels.update(list => {
      const editingId = this.editingChannelId();
      if (!editingId) return [...list, next];
      return list.map(item => (item.id === editingId ? next : item));
    });
    this.resetChannelForm();
  }

  protected onSearch(value: string): void {
    this.search.set(value);
    this.currentPage.set(0);
  }

  protected onPageSizeChange(option: UiSelectOption | null): void {
    const next = Number(option?.value ?? option?.id ?? 10);
    this.currentSize.set(PAGE_SIZES.includes(next) ? next : 10);
    this.currentPage.set(0);
  }

  protected goToPage(page: number): void {
    this.closeActionMenu();
    this.currentPage.set(page);
  }

  protected toggleActionMenu(
    event: MouseEvent,
    contact: UiContactListItem,
    idx: number,
  ): void {
    event.stopPropagation();
    if (this.actionMenu()?.rowIndex === idx) {
      this.closeActionMenu();
      return;
    }

    const trigger = event.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const items = this.resolvedActions().filter(
      action => action.visible?.(contact) !== false,
    );
    const estimatedMenuH = items.length * 36 + 8;
    const openAbove = rect.top + estimatedMenuH > window.innerHeight;

    this.actionMenu.set({
      row: contact,
      rowIndex: idx,
      top: openAbove ? rect.bottom : rect.top,
      left: rect.left,
      openAbove,
      items,
    });
  }

  protected onActionSelect(action: RowAction): void {
    const menu = this.actionMenu();
    this.closeActionMenu();
    if (!menu) return;
    this.actionClick.emit({
      contact: menu.row as UiContactListItem,
      action: action.key,
    });
  }

  protected closeActionMenu(): void {
    this.actionMenu.set(null);
  }

  @HostListener('document:click')
  protected closeActionMenuOnDocumentClick(): void {
    this.closeActionMenu();
  }

  @HostListener('document:keydown.escape')
  protected closeActionMenuOnEscape(): void {
    this.closeActionMenu();
  }

  @HostListener('window:resize')
  protected closeActionMenuOnResize(): void {
    this.closeActionMenu();
  }

  protected safePage(): number {
    return Math.min(this.currentPage(), Math.max(0, this.totalPages() - 1));
  }

  private resetForm(): void {
    this.photoFile.set(null);
    this.photoResetKey.update(key => key + 1);
    this.fullName.set('');
    this.department.set('');
    this.position.set('');
    this.channels.set([]);
    this.selectedNotificationIds.set([]);
    this.resetChannelForm();
    this.channelDrawerOpen.set(false);
  }

  private resetChannelForm(): void {
    this.editingChannelId.set(null);
    this.channelType.set(
      CHANNEL_TYPE_OPTIONS.find(option => option.id === 'mobile') ?? null,
    );
    this.channelValue.set('');
  }
}
